import { normalizeLinkHref } from '@/shared/utils/text-entities'

import { ApplyFormatOptions, ApplyFormatResult, InlineFormatCommand, TypingFormatCommand } from './types'

const FORMAT_TAG_MAP = {
  bold: 'b',
  italic: 'i',
  underline: 'u',
  strikethrough: 's',
} as const

const FORMAT_TAG_SELECTORS = {
  bold: 'b,strong',
  italic: 'i,em',
  underline: 'u',
  strikethrough: 's,strike,del',
} as const

type InlineTagCommand = keyof typeof FORMAT_TAG_MAP

const INLINE_EXEC_COMMAND_MAP = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strikeThrough',
} as const

const FORMAT_PLACEHOLDER = '\u200B'
const CODE_SELECTOR = 'code'

const cleanFormatPlaceholders = (root: DocumentFragment | Node) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let textNode = walker.nextNode()

  while (textNode) {
    const text = textNode.textContent
    if (text?.includes(FORMAT_PLACEHOLDER)) {
      textNode.textContent = text.split(FORMAT_PLACEHOLDER).join('')
    }
    textNode = walker.nextNode()
  }
}

const getClosestCodeElement = (node: Node | null | undefined, editor: HTMLElement): HTMLElement | null => {
  if (!node) return null

  const parent = node instanceof HTMLElement ? node : node.parentElement
  const code = parent?.closest(CODE_SELECTOR)

  return code instanceof HTMLElement && editor.contains(code) ? code : null
}

const getContainingCodeElement = (editor: HTMLElement, range: Range): HTMLElement | null => {
  if (range.collapsed) {
    return getClosestCodeElement(range.startContainer, editor)
  }

  const startCode = getClosestCodeElement(range.startContainer, editor)
  const endCode = getClosestCodeElement(range.endContainer, editor)

  if (!startCode || startCode !== endCode) {
    return null
  }

  const codeRange = document.createRange()
  codeRange.selectNodeContents(startCode)

  const startsInsideCode = range.compareBoundaryPoints(Range.START_TO_START, codeRange) >= 0
  const endsInsideCode = range.compareBoundaryPoints(Range.END_TO_END, codeRange) <= 0

  return startsInsideCode && endsInsideCode ? startCode : null
}

const removeEmptyPlaceholderNodes = (root: ParentNode) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodesToRemove: Text[] = []
  let textNode = walker.nextNode()

  while (textNode) {
    const text = textNode.textContent ?? ''
    if (text === FORMAT_PLACEHOLDER) {
      nodesToRemove.push(textNode as Text)
    }
    textNode = walker.nextNode()
  }

  nodesToRemove.forEach((node) => {
    node.remove()
  })
}

const wrapSelectionInTag = (editor: HTMLElement, tagName: string) => {
  const selection = window.getSelection()
  if (!selection?.rangeCount) return

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer) || range.collapsed) return

  const contents = range.extractContents()
  cleanFormatPlaceholders(contents)

  const element = document.createElement(tagName)
  element.appendChild(contents)
  range.insertNode(element)

  range.selectNodeContents(element)
  selection.removeAllRanges()
  selection.addRange(range)
}

const dispatchInput = (editor: HTMLElement) => {
  editor.dispatchEvent(new Event('input', { bubbles: true }))
}

const unwrapFormatElement = (element: HTMLElement) => {
  const parent = element.parentNode
  if (!parent) return

  const selection = window.getSelection()
  const caretNode = selection?.anchorNode
  const caretOffset = selection?.anchorOffset ?? 0
  const shouldRestoreCaret = caretNode
    ? element.contains(caretNode) || element === caretNode
    : false

  const fragment = document.createDocumentFragment()
  while (element.firstChild) {
    fragment.appendChild(element.firstChild)
  }
  parent.insertBefore(fragment, element)
  parent.removeChild(element)
  cleanFormatPlaceholders(parent)
  removeEmptyPlaceholderNodes(parent)

  if (!selection || !shouldRestoreCaret) return

  const lastNode = fragment.lastChild
  if (lastNode?.nodeType === Node.TEXT_NODE) {
    const range = document.createRange()
    range.setStart(lastNode, Math.min(caretOffset, lastNode.textContent?.length ?? 0))
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
    return
  }

  if (fragment.lastChild) {
    const range = document.createRange()
    range.setStartAfter(fragment.lastChild)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
  }
}

const getInlineFormatElementAtRange = (
  editor: HTMLElement,
  range: Range,
  selector: string,
): HTMLElement | null => {
  const node = range.startContainer
  const parent = node instanceof HTMLElement ? node : node.parentElement
  const tag = parent?.closest(selector)

  return tag instanceof HTMLElement && editor.contains(tag) ? tag : null
}

const isRangeValidInEditor = (editor: HTMLElement, range: Range): boolean => {
  const { startContainer, endContainer } = range

  return startContainer.isConnected
    && endContainer.isConnected
    && editor.contains(startContainer)
    && editor.contains(endContainer)
}

const resolveEditorRange = (
  editor: HTMLElement,
  savedRange?: Range | null,
  fallbackRange?: Range | null,
): Range | null => {
  for (const candidate of [savedRange, fallbackRange]) {
    if (!candidate) continue

    const range = candidate.cloneRange()

    if (!isRangeValidInEditor(editor, range)) continue

    restoreEditorSelection(editor, range)

    if (editor.contains(range.commonAncestorContainer)) {
      return range
    }
  }

  const selection = window.getSelection()
  if (!selection?.rangeCount) return null

  const range = selection.getRangeAt(0)
  if (!isRangeValidInEditor(editor, range)) return null

  return range
}

const findFormatElementAtRange = (
  editor: HTMLElement,
  range: Range | null | undefined,
  selector: string,
): HTMLElement | null => {
  if (!range || !isRangeValidInEditor(editor, range)) return null

  return getInlineFormatElementAtRange(editor, range, selector)
}

const findFormatElementForToggle = (
  editor: HTMLElement,
  range: Range | null | undefined,
  fallbackRange: Range | null | undefined,
  selector: string,
): HTMLElement | null => {
  return findFormatElementAtRange(editor, range, selector)
    ?? findFormatElementAtRange(editor, fallbackRange, selector)
}

const findFormatElementContainingNode = (
  editor: HTMLElement,
  node: Node | null | undefined,
  selector: string,
): HTMLElement | null => {
  if (!node) return null

  const parent = node instanceof HTMLElement ? node : node.parentElement
  const tag = parent?.closest(selector)

  return tag instanceof HTMLElement && editor.contains(tag) ? tag : null
}

const isFormatElementEmpty = (element: HTMLElement): boolean => {
  return (element.textContent ?? '').replace(/\u200B/g, '').length === 0
}

const getSelectorForTagName = (tagName: string): string => {
  if (tagName === 'code') return CODE_SELECTOR

  const entry = Object.entries(FORMAT_TAG_MAP).find(([, mappedTagName]) => mappedTagName === tagName)
  return entry ? FORMAT_TAG_SELECTORS[entry[0] as InlineTagCommand] : tagName
}

const placeCaretInsideFormatElement = (element: HTMLElement): Range => {
  let textNode = element.firstChild
  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
    textNode = document.createTextNode('')
    element.appendChild(textNode)
  }

  const caretRange = document.createRange()
  caretRange.setStart(textNode, textNode.textContent?.length ?? 0)
  caretRange.collapse(true)
  return caretRange
}

const isEmptyFormatElement = (element: Node, selector: string): element is HTMLElement => {
  return element instanceof HTMLElement
    && element.matches(selector)
    && isFormatElementEmpty(element)
}

const isEditorVisuallyEmpty = (editor: HTMLElement): boolean => {
  return (editor.textContent ?? '').replace(/\u200B/g, '').trim().length === 0
}

const clearEmptyEditorPlaceholders = (editor: HTMLElement) => {
  if (!isEditorVisuallyEmpty(editor)) return

  Array.from(editor.childNodes).forEach((node) => {
    if (node.nodeName === 'BR') {
      node.remove()
    }
  })
}

const normalizeCollapsedEditorRange = (editor: HTMLElement, range: Range): Range => {
  if (!range.collapsed || range.startContainer !== editor) return range

  const { startOffset } = range

  if (startOffset > 0) {
    const previousNode = editor.childNodes.item(startOffset - 1)
    if (previousNode instanceof HTMLElement && previousNode.matches('b,strong,i,em,u,s,strike,del,code')) {
      return placeCaretInsideFormatElement(previousNode)
    }
  }

  const nextNode = editor.childNodes.item(startOffset)
  if (nextNode instanceof HTMLElement && isFormatElementEmpty(nextNode)) {
    return placeCaretInsideFormatElement(nextNode)
  }

  return range
}

const removeOrphanEmptyFormatTags = (editor: HTMLElement, keepTag?: HTMLElement | null) => {
  const selectors = ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'code']

  selectors.forEach((selector) => {
    editor.querySelectorAll(selector).forEach((tag) => {
      if (!(tag instanceof HTMLElement)) return
      if (keepTag && tag === keepTag) return
      if (!isFormatElementEmpty(tag)) return

      tag.remove()
    })
  })
}

const getFormatElementContainingCaret = (editor: HTMLElement): HTMLElement | null => {
  const liveRange = getLiveEditorRange(editor)
  if (!liveRange) return null

  const node = liveRange.startContainer
  const parent = node instanceof HTMLElement ? node : node.parentElement
  const tag = parent?.closest('b,strong,i,em,u,s,strike,del,code')

  return tag instanceof HTMLElement && editor.contains(tag) ? tag : null
}

const insertOrReuseCollapsedFormatTag = (editor: HTMLElement, tagName: string) => {
  const selection = window.getSelection()
  if (!selection?.rangeCount) return null

  let range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer) || !range.collapsed) return null

  range = normalizeCollapsedEditorRange(editor, range)
  applyEditorSelection(editor, range)

  const selector = getSelectorForTagName(tagName)
  const existingTag = getInlineFormatElementAtRange(editor, range, selector)
  if (existingTag) {
    const caretRange = placeCaretInsideFormatElement(existingTag)
    applyEditorSelection(editor, caretRange)
    removeOrphanEmptyFormatTags(editor, existingTag)
    return existingTag
  }

  if (range.startContainer === editor) {
    const previousNode = range.startOffset > 0
      ? editor.childNodes.item(range.startOffset - 1)
      : null
    if (previousNode && isEmptyFormatElement(previousNode, selector)) {
      const caretRange = placeCaretInsideFormatElement(previousNode)
      applyEditorSelection(editor, caretRange)
      removeOrphanEmptyFormatTags(editor, previousNode)
      return previousNode
    }

    const nextNode = editor.childNodes.item(range.startOffset)
    if (nextNode && isEmptyFormatElement(nextNode, selector)) {
      const caretRange = placeCaretInsideFormatElement(nextNode)
      applyEditorSelection(editor, caretRange)
      removeOrphanEmptyFormatTags(editor, nextNode)
      return nextNode
    }
  }

  if (isEditorVisuallyEmpty(editor)) {
    const element = insertFormatTagInEmptyEditor(editor, tagName)

    return element
  }

  removeOrphanEmptyFormatTags(editor)

  const element = document.createElement(tagName)
  const placeholder = document.createTextNode('')
  element.appendChild(placeholder)
  range.insertNode(element)

  const caretRange = placeCaretInsideFormatElement(element)
  selection.removeAllRanges()
  selection.addRange(caretRange)

  removeOrphanEmptyFormatTags(editor, element)

  return element
}

const activateBrowserTypingFormat = (
  editor: HTMLElement,
  command: InlineTagCommand,
  caretRange: Range,
) => {
  applyEditorSelection(editor, caretRange)
}

const placeCaretAfterFormatElement = (element: HTMLElement): Range | null => {
  const parent = element.parentNode
  if (!parent) return null

  cleanFormatPlaceholders(element)
  removeEmptyPlaceholderNodes(element)

  if (isFormatElementEmpty(element)) {
    const range = document.createRange()
    range.setStartBefore(element)
    range.collapse(true)
    element.remove()
    return range
  }

  let caretNode = element.nextSibling
  if (!caretNode || caretNode.nodeType !== Node.TEXT_NODE) {
    caretNode = document.createTextNode('')
    parent.insertBefore(caretNode, element.nextSibling)
  }

  const range = document.createRange()
  range.setStart(caretNode, 0)
  range.collapse(true)
  return range
}

const getLiveEditorRange = (editor: HTMLElement): Range | null => {
  const selection = window.getSelection()
  if (!selection?.rangeCount) return null

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer)) return null

  return range
}

const enableCollapsedInlineFormat = (
  editor: HTMLElement,
  command: InlineTagCommand,
  range: Range,
) => {
  const normalizedRange = normalizeCollapsedEditorRange(editor, range)
  applyEditorSelection(editor, normalizedRange)
  insertOrReuseCollapsedFormatTag(editor, FORMAT_TAG_MAP[command])

  const liveRange = getLiveEditorRange(editor)
  if (liveRange) {
    activateBrowserTypingFormat(editor, command, liveRange)
  }

}

export const ensureTypingFormatCaret = (
  editor: HTMLElement | undefined,
  command: TypingFormatCommand,
): boolean => {
  if (!editor) return false

  const isCode = command === 'code'
  const selector = isCode ? CODE_SELECTOR : FORMAT_TAG_SELECTORS[command]
  const tagName = isCode ? 'code' : FORMAT_TAG_MAP[command]
  const liveRange = getLiveEditorRange(editor)

  if (liveRange && getInlineFormatElementAtRange(editor, liveRange, selector)) {
    editor.focus({ preventScroll: true })
    return true
  }

  const tags = editor.querySelectorAll(selector)
  for (let index = tags.length - 1; index >= 0; index -= 1) {
    const tag = tags[index] as HTMLElement
    if (!isFormatElementEmpty(tag)) continue

    const caretRange = placeCaretInsideFormatElement(tag)
    applyEditorSelection(editor, caretRange)
    removeOrphanEmptyFormatTags(editor, tag)
    if (!isCode) {
      activateBrowserTypingFormat(editor, command as InlineTagCommand, caretRange)
    }
    return true
  }

  if (liveRange) {
    applyEditorSelection(editor, liveRange)
    if (isCode) {
      insertOrReuseCollapsedFormatTag(editor, 'code')
    } else {
      insertOrReuseCollapsedFormatTag(editor, tagName)
      const updatedRange = getLiveEditorRange(editor)
      if (updatedRange) {
        activateBrowserTypingFormat(editor, command as InlineTagCommand, updatedRange)
      }
    }
    return true
  }

  return false
}

const resolveCollapsedEditorRange = (
  editor: HTMLElement,
  savedRange?: Range | null,
  fallbackRange?: Range | null,
): Range | null => {
  const resolvedRange = resolveEditorRange(editor, savedRange, fallbackRange)

  if (resolvedRange) {
    const normalizedRange = normalizeCollapsedEditorRange(editor, resolvedRange)
    restoreEditorSelection(editor, normalizedRange)
    return normalizedRange
  }

  const endRange = document.createRange()
  endRange.selectNodeContents(editor)
  endRange.collapse(false)
  restoreEditorSelection(editor, endRange)
  return endRange
}

const applyEditorSelection = (editor: HTMLElement, range: Range | null | undefined) => {
  if (!range) return

  editor.focus({ preventScroll: true })
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

const insertFormatTagInEmptyEditor = (editor: HTMLElement, tagName: string): HTMLElement => {
  clearEmptyEditorPlaceholders(editor)

  const selector = getSelectorForTagName(tagName)
  const existingTag = Array.from(editor.querySelectorAll(selector)).find((tag) => (
    tag instanceof HTMLElement && isFormatElementEmpty(tag)
  ))

  if (existingTag instanceof HTMLElement) {
    const caretRange = placeCaretInsideFormatElement(existingTag)
    applyEditorSelection(editor, caretRange)
    removeOrphanEmptyFormatTags(editor, existingTag)
    return existingTag
  }

  const element = document.createElement(tagName)
  element.appendChild(document.createTextNode(''))
  editor.appendChild(element)

  const caretRange = placeCaretInsideFormatElement(element)
  applyEditorSelection(editor, caretRange)
  removeOrphanEmptyFormatTags(editor, element)

  return element
}

const isBrowserTypingFormatActive = (command: InlineTagCommand): boolean => {
  try {
    return document.queryCommandState(INLINE_EXEC_COMMAND_MAP[command])
  } catch {
    return false
  }
}

const isInsertInputType = (inputType: string): boolean => {
  return inputType === 'insertText'
    || inputType === 'insertCompositionText'
    || inputType === 'insertFromComposition'
}

const INLINE_TYPING_COMMANDS: InlineTagCommand[] = ['bold', 'italic', 'underline', 'strikethrough']

const forceDeactivateBrowserTypingFormat = (
  editor: HTMLElement,
  command: InlineTagCommand,
  caretRange: Range,
): Range => {
  const selector = FORMAT_TAG_SELECTORS[command]
  const savedCaret = caretRange.cloneRange()

  applyEditorSelection(editor, savedCaret)

  if (!isBrowserTypingFormatActive(command)) {
    return savedCaret
  }

  if (!getInlineFormatElementAtRange(editor, savedCaret, selector)) {
    document.execCommand(INLINE_EXEC_COMMAND_MAP[command])
    applyEditorSelection(editor, savedCaret)
  }

  return savedCaret
}

const deactivateBrowserTypingFormat = (
  editor: HTMLElement,
  command: InlineTagCommand,
  caretRange?: Range | null,
): Range | null => {
  const selection = window.getSelection()
  const resolvedCaret = caretRange?.cloneRange()
    ?? (selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null)

  if (!resolvedCaret || !editor.contains(resolvedCaret.commonAncestorContainer)) return null

  if (!isBrowserTypingFormatActive(command)) return resolvedCaret

  return forceDeactivateBrowserTypingFormat(editor, command, resolvedCaret)
}

const TYPING_FORMAT_COMMANDS: TypingFormatCommand[] = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'code',
]

const getFlatTextNodeStartOffset = (editor: HTMLElement, textNode: Text): number => {
  const preRange = document.createRange()
  preRange.selectNodeContents(editor)
  preRange.setEnd(textNode, 0)
  return preRange.toString().length
}

const insertFormatTagAfterNode = (
  editor: HTMLElement,
  refNode: Node,
  tagName: string,
  command: TypingFormatCommand,
): boolean => {
  const isCode = command === 'code'
  const selector = isCode ? CODE_SELECTOR : FORMAT_TAG_SELECTORS[command as InlineTagCommand]
  const parent = refNode.parentNode
  if (!parent) return false

  const nextSibling = refNode.nextSibling
  if (nextSibling && isEmptyFormatElement(nextSibling, selector)) {
    const caretRange = placeCaretInsideFormatElement(nextSibling)
    applyEditorSelection(editor, caretRange)
    removeOrphanEmptyFormatTags(editor, nextSibling as HTMLElement)
    if (!isCode) {
      activateBrowserTypingFormat(editor, command as InlineTagCommand, caretRange)
    }
    return true
  }

  const element = document.createElement(tagName)
  const placeholder = document.createTextNode('')
  element.appendChild(placeholder)
  parent.insertBefore(element, refNode.nextSibling)

  const caretRange = placeCaretInsideFormatElement(element)
  applyEditorSelection(editor, caretRange)
  removeOrphanEmptyFormatTags(editor, element)

  if (!isCode) {
    activateBrowserTypingFormat(editor, command as InlineTagCommand, caretRange)
  }

  return true
}

const resolvePlainTextNodeAtCollapsedRange = (
  editor: HTMLElement,
  range: Range,
): { textNode: Text, caretOffset: number } | null => {
  const { startContainer, startOffset } = range

  if (startContainer.nodeType === Node.TEXT_NODE) {
    return {
      textNode: startContainer as Text,
      caretOffset: startOffset,
    }
  }

  if (startContainer !== editor) return null

  const childAtOffset = editor.childNodes.item(startOffset)
  if (childAtOffset?.nodeType === Node.TEXT_NODE) {
    return { textNode: childAtOffset as Text, caretOffset: 0 }
  }

  if (startOffset > 0) {
    const previousChild = editor.childNodes.item(startOffset - 1)
    if (previousChild?.nodeType === Node.TEXT_NODE) {
      const textNode = previousChild as Text
      return { textNode, caretOffset: textNode.length }
    }
  }

  return null
}

const wrapPlainTextInFormatTag = (
  editor: HTMLElement,
  command: TypingFormatCommand,
  liveRange: Range,
  typingFormatAnchorFlatOffset: number | null,
): boolean => {
  const isCode = command === 'code'
  const selector = isCode ? CODE_SELECTOR : FORMAT_TAG_SELECTORS[command]
  const tagName = isCode ? 'code' : FORMAT_TAG_MAP[command]

  if (getInlineFormatElementAtRange(editor, liveRange, selector)) return false

  const resolved = resolvePlainTextNodeAtCollapsedRange(editor, liveRange)
  if (!resolved) {
    if (!isEditorVisuallyEmpty(editor)) return false

    insertFormatTagInEmptyEditor(editor, tagName)
    if (!isCode) {
      const updatedRange = getLiveEditorRange(editor)
      if (updatedRange) {
        activateBrowserTypingFormat(editor, command as InlineTagCommand, updatedRange)
      }
    }

    return true
  }

  const { textNode, caretOffset } = resolved
  const parent = textNode.parentElement
  if (!parent || !editor.contains(textNode)) return false
  if (parent.closest(selector)) return false

  const fullText = textNode.textContent ?? ''
  const visibleText = fullText.replace(/\u200B/g, '')
  if (!visibleText.length) return false

  const nodeStartFlat = getFlatTextNodeStartOffset(editor, textNode)
  const wrapStartInNode = typingFormatAnchorFlatOffset !== null
    ? Math.max(0, typingFormatAnchorFlatOffset - nodeStartFlat)
    : caretOffset

  if (wrapStartInNode >= fullText.length) {
    return insertFormatTagAfterNode(editor, textNode, tagName, command)
  }

  const prefixText = fullText.slice(0, wrapStartInNode)
  const suffixText = fullText.slice(wrapStartInNode)

  if (!suffixText.replace(/\u200B/g, '').length) {
    return insertFormatTagAfterNode(editor, textNode, tagName, command)
  }

  const element = document.createElement(tagName)
  const suffixNode = document.createTextNode(suffixText)

  if (prefixText.length) {
    textNode.textContent = prefixText
    parent.insertBefore(element, textNode.nextSibling)
  } else {
    parent.replaceChild(element, textNode)
  }

  element.appendChild(suffixNode)

  const caretRange = document.createRange()
  caretRange.setStart(suffixNode, Math.min(Math.max(caretOffset - wrapStartInNode, 0), suffixNode.length))
  caretRange.collapse(true)
  applyEditorSelection(editor, caretRange)

  if (!isCode) {
    activateBrowserTypingFormat(editor, command as InlineTagCommand, caretRange)
  }

  return true
}

export const insertTextAtCollapsedRange = (
  editor: HTMLElement,
  range: Range,
  text: string,
): Range | null => {
  if (!text) return range

  const resolved = resolvePlainTextNodeAtCollapsedRange(editor, range)
  if (resolved) {
    const { textNode, caretOffset } = resolved
    textNode.insertData(caretOffset, text)

    const caretRange = document.createRange()
    caretRange.setStart(textNode, caretOffset + text.length)
    caretRange.collapse(true)
    applyEditorSelection(editor, caretRange)
    return caretRange
  }

  if (range.startContainer === editor) {
    const textNode = document.createTextNode(text)
    const refNode = editor.childNodes.item(range.startOffset)

    if (refNode) {
      editor.insertBefore(textNode, refNode)
    } else {
      editor.appendChild(textNode)
    }

    const caretRange = document.createRange()
    caretRange.setStart(textNode, text.length)
    caretRange.collapse(true)
    applyEditorSelection(editor, caretRange)
    return caretRange
  }

  return null
}

const isCollapsedRangeAtEndOfElement = (element: HTMLElement, range: Range): boolean => {
  if (!range.collapsed) return false

  const container = range.startContainer
  if (container !== element && !element.contains(container)) return false

  const trailing = document.createRange()
  try {
    trailing.setStart(container, range.startOffset)
    trailing.setEnd(element, element.childNodes.length)
  } catch {
    return false
  }

  return trailing.toString().length === 0
}

export const shouldInterceptInactiveFormatInput = (
  editor: HTMLElement | undefined,
  typingFormats: Record<TypingFormatCommand, boolean>,
  inputType: string,
): boolean => {
  if (!editor || !isInsertInputType(inputType)) return false

  const liveRange = getLiveEditorRange(editor)
  if (!liveRange?.collapsed) return false

  return INLINE_TYPING_COMMANDS.some((command) => {
    if (typingFormats[command]) return false
    if (!isBrowserTypingFormatActive(command)) return false

    const formatElement = getInlineFormatElementAtRange(
      editor,
      liveRange,
      FORMAT_TAG_SELECTORS[command],
    )

    // Editing inside existing markup — do not force plain text / jump caret.
    if (formatElement && !isCollapsedRangeAtEndOfElement(formatElement, liveRange)) {
      return false
    }

    return true
  })
}

export const preparePlainTextInsertAtCaret = (
  editor: HTMLElement | undefined,
  typingFormats: Record<TypingFormatCommand, boolean>,
): Range | null => {
  if (!editor) return null

  ensureInactiveFormatCaretOutside(editor, typingFormats)

  INLINE_TYPING_COMMANDS.forEach((command) => {
    if (typingFormats[command]) return

    const liveRange = getLiveEditorRange(editor)
    if (!liveRange || !isBrowserTypingFormatActive(command)) return

    forceDeactivateBrowserTypingFormat(editor, command, liveRange)
  })

  return getLiveEditorRange(editor)
}

export const handleInactiveFormatBeforeInput = (
  editor: HTMLElement | undefined,
  typingFormats: Record<TypingFormatCommand, boolean>,
  event: InputEvent,
): boolean => {
  if (!editor || event.isComposing) return false
  if (!shouldInterceptInactiveFormatInput(editor, typingFormats, event.inputType)) return false

  const text = event.data
  if (!text) return false

  event.preventDefault()

  const range = preparePlainTextInsertAtCaret(editor, typingFormats)
  if (!range) return false

  const caretRange = insertTextAtCollapsedRange(editor, range, text)
  if (!caretRange) return false

  dispatchInput(editor)

  return true
}

export const ensureInactiveFormatCaretOutside = (
  editor: HTMLElement | undefined,
  typingFormats: Record<TypingFormatCommand, boolean>,
): boolean => {
  if (!editor) return false

  const liveRange = getLiveEditorRange(editor)
  if (!liveRange?.collapsed) return false

  let changed = false

  TYPING_FORMAT_COMMANDS.forEach((command) => {
    if (typingFormats[command]) return

    const selector = command === 'code' ? CODE_SELECTOR : FORMAT_TAG_SELECTORS[command]
    const formatElement = getInlineFormatElementAtRange(editor, liveRange, selector)
    if (!formatElement) return
    // Keep caret when editing start/middle of formatted text; exit only at trailing edge
    // (after turning a format off so the next characters stay plain).
    if (!isCollapsedRangeAtEndOfElement(formatElement, liveRange)) return

    const caretRange = placeCaretAfterFormatElement(formatElement)
    if (!caretRange) return

    applyEditorSelection(editor, caretRange)
    changed = true
  })

  return changed
}

export const reconcileTypingFormatDom = (
  editor: HTMLElement | undefined,
  typingFormats: Record<TypingFormatCommand, boolean>,
  typingFormatAnchorFlatOffset: number | null = null,
): boolean => {
  if (!editor) return false

  let changed = false
  const liveRange = getLiveEditorRange(editor)
  if (!liveRange?.collapsed) return false

  TYPING_FORMAT_COMMANDS.forEach((command) => {
    if (!typingFormats[command]) return
    if (wrapPlainTextInFormatTag(editor, command, liveRange, typingFormatAnchorFlatOffset)) {
      changed = true
    }
  })

  return changed
}

export const syncInactiveBrowserTypingFormats = (
  editor: HTMLElement | undefined,
  typingFormats: Record<TypingFormatCommand, boolean>,
) => {
  preparePlainTextInsertAtCaret(editor, typingFormats)
}

const exitTypingFormatAtRange = (
  editor: HTMLElement,
  command: InlineTagCommand,
  range: Range,
  options?: ApplyFormatOptions,
): Range | null => {
  applyEditorSelection(editor, range)

  const liveRange = getLiveEditorRange(editor) ?? range
  const selector = FORMAT_TAG_SELECTORS[command]
  const tagToExit = getInlineFormatElementAtRange(editor, liveRange, selector)
    ?? findFormatElementAtRange(editor, options?.fallbackRange, selector)
    ?? findFormatElementContainingNode(editor, options?.fallbackRange?.startContainer, selector)

  let caretRange: Range | null = null

  if (tagToExit) {
    caretRange = placeCaretAfterFormatElement(tagToExit)
    applyEditorSelection(editor, caretRange)
  } else {
    caretRange = getLiveEditorRange(editor)?.cloneRange() ?? null
  }

  const finalCaret = caretRange
    ? deactivateBrowserTypingFormat(editor, command, caretRange) ?? caretRange
    : null

  if (finalCaret) {
    applyEditorSelection(editor, finalCaret)
  }

  removeOrphanEmptyFormatTags(editor, getFormatElementContainingCaret(editor))

  return finalCaret
}

const toggleCollapsedInlineFormat = (
  editor: HTMLElement,
  command: InlineTagCommand,
  range: Range,
  options?: ApplyFormatOptions,
): ApplyFormatResult => {
  if (!range.collapsed || !editor.contains(range.commonAncestorContainer)) return null

  const shouldTurnOff = !!options?.turnOff

  if (shouldTurnOff) {
    exitTypingFormatAtRange(editor, command, range, options)
    dispatchInput(editor)
    return { format: command, enabled: false }
  }

  enableCollapsedInlineFormat(editor, command, range)
  return { format: command, enabled: true }
}

const toggleCodeFormat = (
  editor: HTMLElement,
  savedRange?: Range | null,
  options?: ApplyFormatOptions,
): ApplyFormatResult => {
  const range = options?.turnOff
    ? resolveEditorRange(editor, savedRange, options?.fallbackRange)
    : resolveCollapsedEditorRange(editor, savedRange, options?.fallbackRange)
  if (!range || !editor.contains(range.commonAncestorContainer)) return null

  const existingCode = getContainingCodeElement(editor, range)
    ?? findFormatElementForToggle(editor, range, options?.fallbackRange, CODE_SELECTOR)
    ?? findFormatElementContainingNode(editor, options?.fallbackRange?.startContainer, CODE_SELECTOR)

  if (existingCode && range.collapsed && options?.turnOff) {
    applyEditorSelection(editor, placeCaretAfterFormatElement(existingCode))
    cleanFormatPlaceholders(editor)
    dispatchInput(editor)
    return { format: 'code', enabled: false }
  }

  if (existingCode) {
    if (range.collapsed) {
      return null
    }

    unwrapFormatElement(existingCode)
    dispatchInput(editor)
    return { format: 'code', enabled: false }
  }

  if (range.collapsed) {
    applyEditorSelection(editor, range)
    insertOrReuseCollapsedFormatTag(editor, 'code')
    return { format: 'code', enabled: true }
  }

  wrapSelectionInTag(editor, 'code')
  dispatchInput(editor)
  return null
}

const isFormatActiveAtSelection = (
  command: InlineTagCommand,
  parentElement: Element | null | undefined,
) => {
  const selector = FORMAT_TAG_SELECTORS[command]

  return !!parentElement?.closest(selector)
}

export const cleanEditorFormatPlaceholders = (editor: HTMLElement | undefined): boolean => {
  if (!editor) return false

  const before = editor.innerHTML
  cleanFormatPlaceholders(editor)
  removeEmptyPlaceholderNodes(editor)
  removeOrphanEmptyFormatTags(editor, getFormatElementContainingCaret(editor))

  return editor.innerHTML !== before
}

export const saveEditorSelection = (editor: HTMLElement | undefined): Range | null => {
  if (!editor) return null

  const selection = window.getSelection()
  if (!selection?.rangeCount) return null

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer)) return null

  return range.cloneRange()
}

export type EditorSelectionSnapshot = {
  startPath: number[]
  startOffset: number
  endPath: number[]
  endOffset: number
}

const getNodePath = (editor: HTMLElement, node: Node): number[] | null => {
  const path: number[] = []
  let current: Node | null = node

  while (current !== null && current !== editor) {
    const parent: ParentNode | null = current.parentNode
    if (parent === null) return null

    path.unshift(Array.prototype.indexOf.call(parent.childNodes, current))
    current = parent
  }

  return current === editor ? path : null
}

const resolveNodePath = (editor: HTMLElement, path: number[]): Node | null => {
  let current: Node = editor

  for (const index of path) {
    const child: ChildNode | null = current.childNodes.item(index)
    if (child === null) return null
    current = child
  }

  return current
}

export const snapshotEditorSelection = (editor: HTMLElement | undefined): EditorSelectionSnapshot | null => {
  const range = saveEditorSelection(editor)
  if (!range || !editor) return null

  const startPath = getNodePath(editor, range.startContainer)
  const endPath = getNodePath(editor, range.endContainer)
  if (!startPath || !endPath) return null

  return {
    startPath,
    startOffset: range.startOffset,
    endPath,
    endOffset: range.endOffset,
  }
}

export const restoreEditorSelectionSnapshot = (
  editor: HTMLElement | undefined,
  snapshot: EditorSelectionSnapshot | null,
): Range | null => {
  const range = createRangeFromEditorSelectionSnapshot(editor, snapshot)
  if (!range) return null

  restoreEditorSelection(editor, range)
  return range
}

export const createRangeFromEditorSelectionSnapshot = (
  editor: HTMLElement | undefined,
  snapshot: EditorSelectionSnapshot | null,
): Range | null => {
  if (!editor || !snapshot) return null

  const startContainer = resolveNodePath(editor, snapshot.startPath)
  const endContainer = resolveNodePath(editor, snapshot.endPath)
  if (!startContainer || !endContainer) return null

  const range = document.createRange()

  try {
    range.setStart(startContainer, snapshot.startOffset)
    range.setEnd(endContainer, snapshot.endOffset)
  } catch {
    return null
  }

  return range
}

export const restoreEditorSelection = (editor: HTMLElement | undefined, savedRange: Range | null) => {
  if (!editor || !savedRange) return

  editor.focus({ preventScroll: true })
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(savedRange)
}

export const getEditorSelectedText = (editor: HTMLElement | undefined): string => {
  if (!editor) return ''

  const selection = window.getSelection()
  if (!selection?.rangeCount) return ''

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer)) return ''

  return range.toString()
}

export const getSelectedLinkElement = (editor: HTMLElement | undefined): HTMLAnchorElement | null => {
  if (!editor) return null

  const selection = window.getSelection()
  if (!selection?.rangeCount) return null

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer)) return null

  const anchorNode = selection.anchorNode
  const parentElement = anchorNode instanceof HTMLElement
    ? anchorNode
    : anchorNode?.parentElement
  const anchor = parentElement?.closest('a')

  if (!anchor || !editor.contains(anchor)) return null

  return anchor
}

export const removeLinkElement = (anchor: HTMLAnchorElement): boolean => {
  anchor.replaceWith(document.createTextNode(anchor.textContent ?? ''))
  return true
}

export const updateLinkElement = (
  anchor: HTMLAnchorElement,
  url: string,
  text?: string,
): boolean => {
  const href = normalizeLinkHref(url)
  if (!href) return false

  anchor.href = href
  anchor.rel = 'noopener noreferrer'
  anchor.target = '_blank'
  anchor.className = 'text-link'
  anchor.textContent = text?.trim() || anchor.textContent?.trim() || href

  return true
}

export const insertLinkAtSelection = (
  editor: HTMLElement,
  url: string,
  text?: string,
): boolean => {
  const href = normalizeLinkHref(url)
  if (!href) return false

  const selection = window.getSelection()
  if (!selection?.rangeCount) return false

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer)) return false

  const selectedText = range.toString()
  const anchor = document.createElement('a')
  anchor.href = href
  anchor.rel = 'noopener noreferrer'
  anchor.target = '_blank'
  anchor.className = 'text-link'
  anchor.textContent = text?.trim() || selectedText || href

  range.deleteContents()
  range.insertNode(anchor)
  range.setStartAfter(anchor)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)

  return true
}

export const applyInlineFormatCommand = (
  editor: HTMLElement | undefined,
  command: InlineFormatCommand,
  savedRange?: Range | null,
  options?: ApplyFormatOptions,
): ApplyFormatResult => {
  if (!editor) return null

  if (command === 'code') {
    return toggleCodeFormat(editor, savedRange, options)
  }

  if (command === 'removeFormat') {
    editor.focus()
    document.execCommand('removeFormat')
    dispatchInput(editor)
    return { format: 'all', enabled: false }
  }

  const range = options?.turnOff
    ? resolveEditorRange(editor, savedRange, options?.fallbackRange)
    : resolveCollapsedEditorRange(editor, savedRange, options?.fallbackRange)
  if (!range) {
    if (options?.turnOff) {
      deactivateBrowserTypingFormat(editor, command)
    }
    return options?.turnOff
      ? { format: command, enabled: false }
      : null
  }

  if (range.collapsed) {
    return toggleCollapsedInlineFormat(editor, command, range, options)
  }

  editor.focus()
  document.execCommand(INLINE_EXEC_COMMAND_MAP[command])
  dispatchInput(editor)
  return null
}

export const queryActiveTextFormats = (editor: HTMLElement | undefined) => {
  const inactive = {
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    code: false,
    link: false,
  }

  if (!editor) return inactive

  const selection = window.getSelection()
  if (!selection?.rangeCount) return inactive

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer)) return inactive

  const anchorNode = selection.anchorNode
  const parentElement = anchorNode instanceof HTMLElement
    ? anchorNode
    : anchorNode?.parentElement

  return {
    bold: isFormatActiveAtSelection('bold', parentElement),
    italic: isFormatActiveAtSelection('italic', parentElement),
    underline: isFormatActiveAtSelection('underline', parentElement),
    strikethrough: isFormatActiveAtSelection('strikethrough', parentElement),
    code: !!parentElement?.closest('code'),
    link: !!getSelectedLinkElement(editor),
  }
}

export const insertTextAtCaret = (editor: HTMLElement | undefined, text: string) => {
  if (!editor) return

  editor.focus()
  document.execCommand('insertText', false, text)
  dispatchInput(editor)
}
